import { useSyncExternalStore } from 'react';

import { SAMPLE_PROJECT_ENABLED } from '@/config';

/**
 * Whether the portfolio offers "Create sample (Project Ship)" — as a RUNTIME
 * toggle, so it can be flipped from the sidebar instead of by restarting the
 * dev server.
 *
 * `VITE_SAMPLE_PROJECT` cannot do that job alone: Vite inlines
 * `import.meta.env` when the server boots, so the env var can only ever be the
 * *default*. This store layers a localStorage override on top of it, and the
 * sidebar's dev control writes that override.
 *
 * A module-level store rather than `usePersistedState` because two components
 * in different subtrees have to agree: the sidebar toggles it, the portfolio
 * page renders from it, and `usePersistedState` keeps its value in each
 * component's own `useState` — the button would not appear until a reload.
 */
const STORAGE_KEY = 'pm.devTools.sampleProject';

const listeners = new Set<() => void>();

/*
  Guarded like `usePersistedState`'s: localStorage throws in private-mode Safari
  and on an exhausted quota. Losing a dev preference is acceptable; taking the
  page down with it is not.
*/
const readStored = (): boolean => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === null ? SAMPLE_PROJECT_ENABLED : stored === 'true';
  } catch {
    return SAMPLE_PROJECT_ENABLED;
  }
};

/*
  `getSnapshot` is called on every render, so the value is cached rather than
  re-read from localStorage each time. Seeded lazily: reading at module scope
  would run before a test could seed storage.
*/
let cached: boolean | null = null;

const getSnapshot = (): boolean => {
  cached ??= readStored();
  return cached;
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const setSampleProjectEnabled = (next: boolean): void => {
  cached = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, String(next));
  } catch {
    // The override is lost on reload; the toggle still works for this session.
  }
  for (const listener of listeners) listener();
};

export const useSampleProjectEnabled = (): boolean =>
  useSyncExternalStore(subscribe, getSnapshot, () => SAMPLE_PROJECT_ENABLED);
